/**
 * A list of `column = value` pairs, edited by {@link ColumnValueListControl}.
 * The `bigquery-sql/update` tool takes two of these — `set` (columns to write)
 * and `where` (equality conditions) — each an array of `{ column, value }`.
 *
 * `value` is a template string (literal text and/or `{{ref}}` tokens), the same
 * form every other tool input uses; it is resolved to a value by the runner and
 * bound as a query parameter by the tool, never spliced into SQL.
 */

export type ColumnValuePair = {
  readonly column: string;
  readonly value: string;
};

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

/** Reads a saved `set`/`where` param back into editable pairs. */
export const parsePairs = (value: unknown): ColumnValuePair[] => {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.map((entry) => {
    const rec = asRecord(entry) ?? {};
    const column = typeof rec['column'] === 'string' ? rec['column'] : '';
    const raw = rec['value'];
    return {
      column,
      value: raw === null || raw === undefined ? '' : String(raw),
    };
  });
};

export const PairListOps = {
  add: (pairs: readonly ColumnValuePair[]): ColumnValuePair[] => [
    ...pairs,
    { column: '', value: '' },
  ],
  removeAt: (
    pairs: readonly ColumnValuePair[],
    index: number
  ): ColumnValuePair[] => pairs.filter((_, i) => i !== index),
  setColumn: (
    pairs: readonly ColumnValuePair[],
    index: number,
    column: string
  ): ColumnValuePair[] =>
    pairs.map((pair, i) => (i === index ? { ...pair, column } : pair)),
  setValue: (
    pairs: readonly ColumnValuePair[],
    index: number,
    value: string
  ): ColumnValuePair[] =>
    pairs.map((pair, i) => (i === index ? { ...pair, value } : pair)),
};

/** The shape the tool takes: a plain array of `{ column, value }`. */
export const serializePairs = (
  pairs: readonly ColumnValuePair[]
): { column: string; value: string }[] =>
  pairs.map((pair) => ({ column: pair.column, value: pair.value }));
